import React, { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';

interface SaltParticlesProps {
  position?: [number, number, number];
  beakerInnerRadius?: number;
  beakerBottomY?: number;
  totalSaltG?: number;
  dissolvedFraction?: number;
  isStirring?: boolean;
}

const PARTICLE_COUNT = 120;

export function SaltParticles({
  position = [0, 0, 0],
  beakerInnerRadius = 0.16,
  beakerBottomY = 0,
  totalSaltG = 0,
  dissolvedFraction = 0,
  isStirring = false,
}: SaltParticlesProps) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const seeds = useMemo(() => {
    return Array.from({ length: PARTICLE_COUNT }, () => {
      const r = Math.sqrt(Math.random()) * beakerInnerRadius * 0.7;
      const theta = Math.random() * Math.PI * 2;
      return {
        x: Math.cos(theta) * r,
        z: Math.sin(theta) * r,
        baseY: Math.random() * 0.012,
        size: 0.0035 + Math.random() * 0.0025,
        phase: Math.random() * Math.PI * 2,
      };
    });
  }, [beakerInnerRadius]);

  useFrame((state) => {
    if (!ref.current) return;
    const undissolvedFraction = 1 - dissolvedFraction;
    const visibleCount = totalSaltG > 0 ? Math.round(PARTICLE_COUNT * undissolvedFraction) : 0;
    const t = state.clock.elapsedTime;

    for (let i = 0; i < PARTICLE_COUNT; i++) {
      const s = seeds[i];
      if (i < visibleCount) {
        let x = s.x;
        let z = s.z;
        if (isStirring) {
          const r = Math.hypot(x, z);
          const angle = Math.atan2(z, x);
          x = Math.cos(angle + t * 1.2 + s.phase) * r;
          z = Math.sin(angle + t * 1.2 + s.phase) * r;
        }
        dummy.position.set(x, beakerBottomY + s.baseY, z);
        dummy.scale.setScalar(s.size);
        dummy.rotation.set(s.phase, t * 0.5 + s.phase, 0);
      } else {
        dummy.position.set(0, -999, 0);
        dummy.scale.setScalar(0);
      }
      dummy.updateMatrix();
      ref.current.setMatrixAt(i, dummy.matrix);
    }
    ref.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <group position={position}>
      <instancedMesh ref={ref} args={[undefined, undefined, PARTICLE_COUNT]} castShadow>
        <boxGeometry args={[1, 1, 1]} />
        <meshStandardMaterial color="#fafafa" roughness={0.8} metalness={0.05} />
      </instancedMesh>
    </group>
  );
}

export default SaltParticles;
